import CardCommon from '@op/components/CardCommon';
import { HeadingsNavigator } from '@op/pages/report/gptReport/components/SmallComponents';
import { useSimpleGptData } from '@op/pages/report/gptReport/hooks';
import { handleContentScroll, handleNavigatorScroll } from '@op/utils';
import { downloadHtmlToFile } from '@op/utils/file';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { updateURL, useMenuInsightReportList } from '../../hooks';
import ReportFilter from '../ReportFilter';
import { useLocation } from 'umi';
import { ReportHeaderTitle } from '../../CustomAISummary/component/GPTSummaryPreview';
import { PromptConfirmItemType } from '@op/components/GPT/types';
import moment from 'moment';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint'; 
import { Button } from 'tea-component';
import useReportLanguage from '@op/pages/report/hooks/useReportLanguage';
import DOMPurify from 'dompurify';
import useLanguage from '@op/hooks/useLanguage';

type ReportGptProps = {
  wholeGameInfo: any;
  contentRef: any;
  scrolling: boolean;
  formatMessage: any;
  pageName: string;
};
export default function ReportGpt({
  gameInfo,
  defaultParams,
  pageName,
  ...props
}: ReportGptProps & { gameInfo: any; defaultParams: any }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paramsLanguage = params.get('language');
  const start_time = params.get('report_start_time');
  const end_time = params.get('report_end_time');

  const {
    loading: pageLoading,
    isEmpty,
    language,
    setLanguage,
    ...filterProps
  } = useMenuInsightReportList({
    gameInfo,
    defaultParams,
    defaultLanguage: paramsLanguage,
  });

  const [date, setDate] = useState<any>({
    start_time,
    end_time,
  });
  const [defaultValue, setDefaultValue] = useState({
    start_time,
    end_time,
  });

  const { options, loading } = useReportLanguage({
    gameInfo,
    ...defaultParams,
    setLanguage,
  });

  const filter = useMemo(() => {
    if (!date?.start_time || loading) {
      return {};
    }

    return {
      ...date,
      ...gameInfo,
      language,
      kind: Number(defaultParams.kind),
      type: Number(defaultParams.type),
    };
  }, [language, date, loading]);

  const dropdownRef = useRef<{
    handleRecentReport: (recentReport: any) => void;
  }>(null);
  const handleRecentReport = (recentReport: any = {}) => {
    dropdownRef?.current?.handleRecentReport(recentReport);
  };

  return (
    <CardCommon
      loading={pageLoading}
      empty={isEmpty}
      emptyHeight={600}
      loadingHeight={600}
      contentCard
    >
      <ReportFilter
        pageName={pageName}
        confirm={(value) => {
          setDate({
            start_time: value?.start_time,
            end_time: value?.end_time,
          });
          updateURL({
            updateParams: {
              report_start_time: value?.start_time,
              report_end_time: value?.end_time,
            },
          });
        }}
        {...filterProps}
        options={options}
        language={language}
        setLanguage={(value) => {
          setLanguage?.(value);
          updateURL({
            updateParams: { language: value },
          });
        }}
        hasLanguage
        defaultValue={defaultValue}
        dropdownRef={dropdownRef}
      ></ReportFilter>
      <GptBody
        pageName={pageName}
        {...props}
        filter={filter}
        defaultValue={defaultValue}
        setDefaultValue={(value) => {
          setDefaultValue(value);
          setDate(value);
        }}
        handleRecentReport={handleRecentReport}
      ></GptBody>
    </CardCommon>
  );
}

function GptBody({
  filter,
  wholeGameInfo,
  contentRef,
  scrolling,
  pageName,
  defaultValue,
  setDefaultValue,
  handleRecentReport,
}: ReportGptProps & {
  filter: any;
  defaultValue?: any;
  setDefaultValue: any;
  handleRecentReport?: any;
}) {
  const { formatMessage } = useLanguage();
  const markdownRef = useRef<HTMLDivElement>(null);
  const {
    mdFileUrl,
    renderContentInfo,
    loading,
    navigator,
    setNavigator,
    recentReport,
  } = useSimpleGptData({
    filter,
    defaultValue,
    setDefaultValue,
  });

  const download = () => {
    const gameName = wholeGameInfo?.entity_name || '';
    const fileName = `Opinions_${gameName}_GPT_Auto_Reports_${moment(
      filter.start_time,
    ).format('YYYYMMDD')}`;
    downloadHtmlToFile({
      fileName,
      ref: markdownRef,
      url: mdFileUrl,
      catchCallback: () => setRendering(false),
      finallyCallback: () => setRendering(false),
      thenCallback: () => setRendering(false),
    });
    buriedBtnPoint('DownloadComments', pageName);
  };

  function scrollNavigator() {
    const value = handleContentScroll(
      renderContentInfo.defaultNavigator,
      renderContentInfo.headings.map((item: any) => item.slug),
      contentRef as any,
      10,
    );

    setNavigator(value);
  }

  const [rendering, setRendering] = useState(false);

  const promptInfo: PromptConfirmItemType = useMemo(() => {
    return {
      gptOptions: mdFileUrl ? 'advanced' : 'basic',
    };
  }, [mdFileUrl]);

  useEffect(() => {
    if (scrolling) {
      scrollNavigator();
    }
  }, [scrolling]);

  function downloadPdf() {
    if (!rendering) {
      setRendering(true);
      // 让加载态先渲染出来
      setTimeout(() => {
        download();
      }, 0);
    }
  }

  return (
    <CardCommon
      className="dbo-report-gpt"
      title={
        <ReportHeaderTitle
          promptInfo={promptInfo}
          downloadAdvanceComments={downloadPdf}
          formatMessage={formatMessage}
          loading={rendering}
          downloadText="Download PDF Document"
          hasResult
        />
      }
      ref={contentRef}
      loading={loading || !filter}
      loadingHeight={600}
      empty={!mdFileUrl}
      emptyHeight={600}
      emptyDescElement={
        <Button
          type="link"
          style={{ paddingLeft: 5 }}
          onClick={() => handleRecentReport?.(recentReport)}
        >
          {formatMessage({ id: 'Click to view recent report' })}
        </Button>
      }
    >
      <div className="dbo-gpt-markdown-main">
        <div
          className="markdown-body"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderContentInfo.html) }}
          ref={markdownRef}
        ></div>
      </div>
      <HeadingsNavigator
        list={renderContentInfo.headings}
        navigator={navigator}
        wholeGameInfo={wholeGameInfo}
        contentRef={contentRef}
        handleNavigatorScroll={handleNavigatorScroll}
      ></HeadingsNavigator>
    </CardCommon>
  );
}
